/**
 * The rooms a visitor has, and the order they are offered in.
 *
 * A room is a link to one conversation, so there is nothing inside one to nest:
 * the list is flat, and it is two things joined — the rooms this account
 * created, which it keeps, and the rooms this browser has recently been in.
 * Part 1 §02 asks for exactly that pair: "то, куда вы недавно заходили и что
 * создали".
 *
 * The visits are this browser's rather than the account's. There is no table of
 * who has been where, and inventing one to answer a list is a larger question
 * than the list is worth (#521); a browser already knows where it has been, and
 * a person's own machine is where that memory is least surprising.
 *
 * Pure state: the storage is passed in, so the rule is provable without a
 * browser and the layer below React stays framework-free.
 */

/** How many visits are remembered. Beyond this the oldest is forgotten. */
export const VISITS_KEPT = 12;

/** A room on the list, however it got there. */
export interface SavedRoom {
  code: string;
  /** Epoch ms of the most recent thing known about it. Newest first. */
  at: number;
  /** True where this account created it, which is what keeps it alive. */
  owned: boolean;
  /** The name it was given, where the account knows one. */
  name?: string;
  /** What kind of room it is, where the account knows. */
  mode?: string;
}

/** A visit, as the browser remembers one. */
export interface RoomVisit {
  code: string;
  at: number;
}

/** Where the visits are kept. The name is part of the contract with the browser. */
export const VISITS_KEY = "syncani.visitedRooms";

/** The narrow shape of `localStorage` this needs, so a test can hand one over. */
export interface VisitStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

function isVisit(value: unknown): value is RoomVisit {
  if (typeof value !== "object" || value === null) return false;
  return (
    "code" in value &&
    typeof value.code === "string" &&
    "at" in value &&
    typeof value.at === "number"
  );
}

/**
 * What this browser remembers, newest first.
 *
 * Anything unreadable is nothing rather than a throw. Storage is a place other
 * software writes too, and a visitor whose list is empty is in a worse place
 * than one whose list is wrong — but a visitor looking at a crash is in the
 * worst place of the three.
 */
export function readVisits(store: VisitStore | null | undefined): RoomVisit[] {
  if (!store) return [];
  try {
    const raw = store.getItem(VISITS_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isVisit).sort((a, b) => b.at - a.at);
  } catch {
    return [];
  }
}

/**
 * Records a visit and returns what is now remembered.
 *
 * A code visited twice moves rather than appears twice — the list is places,
 * not events, and two lines for one room would say the room is two rooms.
 */
export function rememberVisit(
  store: VisitStore | null | undefined,
  code: string,
  at: number,
): RoomVisit[] {
  const trimmed = code.trim().toLowerCase();
  if (!store || !trimmed) return readVisits(store);
  const kept = [
    { code: trimmed, at },
    ...readVisits(store).filter((visit) => visit.code !== trimmed),
  ].slice(0, VISITS_KEPT);
  try {
    store.setItem(VISITS_KEY, JSON.stringify(kept));
  } catch {
    // A full or forbidden storage costs the visitor their list, and nothing
    // else. It is not worth failing the thing they were actually doing.
  }
  return kept;
}

/**
 * The list, from what the account holds and what the browser remembers.
 *
 * Owned wins the merge: a room this account created is one it keeps, and that
 * is the more durable of the two facts about the same code. Ordered by the most
 * recent thing known about each — a room somebody was in this morning belongs
 * above one they made last month and never opened.
 */
export function savedRooms(
  owned: readonly { code: string; at: number; name?: string; mode?: string }[],
  visits: readonly RoomVisit[],
): SavedRoom[] {
  const rooms = new Map<string, SavedRoom>();
  for (const visit of visits) {
    rooms.set(visit.code, { code: visit.code, at: visit.at, owned: false });
  }
  for (const room of owned) {
    const seen = rooms.get(room.code);
    rooms.set(room.code, {
      code: room.code,
      at: Math.max(room.at, seen?.at ?? 0),
      owned: true,
      name: room.name,
      mode: room.mode,
    });
  }
  return [...rooms.values()].sort((a, b) => b.at - a.at);
}

/**
 * Forgets a code this browser has been in, and returns what is left.
 *
 * A room that is gone from the database must also be gone from the list, or
 * the visit would keep offering a door onto nothing.
 */
export function forgetVisit(
  store: VisitStore | null | undefined,
  code: string,
): RoomVisit[] {
  const trimmed = code.trim().toLowerCase();
  const kept = readVisits(store).filter((visit) => visit.code !== trimmed);
  if (!store || !trimmed) return kept;
  try {
    store.setItem(VISITS_KEY, JSON.stringify(kept));
  } catch {
    // Same bargain as remembering: a storage that refuses costs the list, and
    // nothing the visitor was actually doing.
  }
  return kept;
}
